import React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight } from "lucide-react";

// ============================================================================
// GAZEAL ELITE FAQ
// ============================================================================

const FAQ_ITEMS = [
{
  question: "What is Gazeal Elite?",
  answer:
  "Gazeal Elite is a private sales service built for agents working at the top of the market, on listings priced at £700,000 and above. It brings together higher reservation commitments, early commission access, and dedicated account support in one place. The principle is simple: the most valuable sales in your pipeline should be the most protected."
},
{
  question: "How is Gazeal Elite different from standard Gazeal?",
  answer:
  "Standard Gazeal is used by a long list of agents across the country to secure sales on properties at every price point, with reservation commitments that sit comfortably at around £2,000. That is plenty on an average listing, but on a £2 million or £5 million sale the maths simply doesn't work the same way. A £2,000 commitment does not reflect what a buyer or seller stands to lose if they change their mind mid transaction, and it certainly doesn't reflect the kind of work you put in to get the deal over the line.\n\nGazeal Elite was built for that end of the market. It lets you reserve a high value property with a commitment that actually matches the size of the transaction, all the way up to £250,000. You and your clients agree the level that makes sense for the deal, Gazeal guarantees it, and everyone involved behaves accordingly because there is real money on the line."
},
{
  question: "What if I've taken a commission advance and the sale falls through?",
  answer:
  "With a Gazeal Reservation Agreement in place this is genuinely rare. Our agreements deliver a 95% success rate to completion, which gives you the strongest odds in the market by a long way.\n\nIn the unlikely event that a sale does fall through, you are not left scrambling to return the commission in one hit. We sit down with you and structure it in a way that works for your business, whether that is offsetting against your next completed sale or agreeing terms that fit how your cash flow runs. Elite is built as a long term partnership with the agents we work with, and we handle these moments in the same spirit."
},
{
  question: "How does commission advance work on Elite?",
  answer:
  "Once a sale is secured with an Elite Reservation Agreement you can draw down your commission the moment the sale is agreed, rather than waiting for the chain to complete. On a premium deal that can mean tens or hundreds of thousands of pounds landing in your business account months ahead of the old timeline. You decide how much to take, and what stays in reserve."
},
{
  question: "What commitment levels are available on Elite?",
  answer:
  "Elite commitment levels run from £10,000 all the way up to £250,000, set on a per deal basis to reflect the value of the property and the seriousness of the parties. You and your clients decide what level fits each transaction, and Gazeal guarantees it so nobody has to put the money down upfront."
},
{
  question: "Can I still use standard Gazeal if my listings are under £700k?",
  answer:
  "Absolutely. Standard Gazeal is available on every listing and every agent we work with has access to it. You can comfortably mix the two, using standard Gazeal for your sub £700k sales and Elite on your premium listings, all inside the same relationship."
}];


export default function EliteFAQ() {
  const handleUnlock = () => {
    const el = document.getElementById("access-section");
    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset - 90;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <section
      id="faq"
      className="relative py-16 md:py-24 bg-black overflow-hidden"
      data-testid="elite-faq-section">

      {/* Ambient warm backdrop */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#FF914D]/[0.03] to-transparent pointer-events-none" />
      <div className="pointer-events-none absolute right-1/4 bottom-0 h-[320px] w-[420px] bg-[#FFDE59]/5 blur-3xl opacity-40" />

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 lg:gap-16">

          {/* Sticky column */}
          <div>
            <div className="lg:sticky lg:top-32">
              <h2 className="text-3xl md:text-4xl lg:text-[44px] font-didot italic leading-[1.15] mb-4">
                <span className="text-white">Common </span>
                <span className="bg-gradient-to-r from-[#FFDE59] to-[#FF914D] bg-clip-text text-transparent">Questions</span>
              </h2>

              <p className="text-white/55 text-sm leading-relaxed mb-6 font-manrope">
                Everything you need to know about Gazeal Elite and how it sits alongside standard Gazeal.
              </p>

              <div className="p-4 bg-gradient-to-br from-[#FFDE59]/10 to-[#FF914D]/5 border border-[#FF914D]/20 rounded-xl">
                <p className="text-white/70 text-xs mb-1 font-manrope">Still have questions?</p>
                <a
                  href="mailto:info@gazeal.co.uk"
                  className="bg-gradient-to-r from-[#FFDE59] to-[#FF914D] bg-clip-text text-transparent font-semibold text-sm hover:opacity-80 font-manrope"
                  data-testid="elite-faq-contact">
                  info@gazeal.co.uk →
                </a>
              </div>
            </div>
          </div>

          {/* Accordion */}
          <div>
            <Accordion type="single" collapsible className="space-y-2">
              {FAQ_ITEMS.map((item, index) =>
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border border-white/[0.08] bg-white/[0.02] px-5 rounded-xl data-[state=open]:border-[#FF914D]/30 data-[state=open]:bg-gradient-to-br data-[state=open]:from-[#FF914D]/[0.05] data-[state=open]:to-transparent transition-all overflow-hidden"
                data-testid={`elite-faq-item-${index}`}>

                  <AccordionTrigger className="text-left text-white hover:text-[#FFDE59] hover:no-underline py-4 text-[15px] font-medium font-manrope">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-white/65 pb-4 leading-relaxed text-sm font-manrope whitespace-pre-line">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              )}

              {/* Apply Item — renders as a CTA button instead of a Q&A */}
              <div
                className="border border-[#FF914D]/25 bg-gradient-to-br from-[#FFDE59]/[0.06] to-[#FF914D]/[0.03] rounded-xl px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                data-testid="elite-faq-apply-card">
                <div>
                  <div className="text-white font-medium text-[15px] font-manrope mb-1">
                    How do I apply for Elite access?
                  </div>
                  <div className="text-white/55 text-sm font-manrope">
                    Start your application in seconds.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleUnlock}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#FFDE59] to-[#FF914D] text-black font-semibold px-5 py-2.5 text-sm hover:opacity-90 transition-opacity font-manrope shadow-[0_4px_15px_rgba(255,145,77,0.25)] whitespace-nowrap"
                  data-testid="elite-faq-unlock-button">
                  Unlock Access
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </Accordion>
          </div>
        </div>
      </div>
    </section>);

}
